import { css } from '@emotion/react'
import { Cell, DropdownItem, Grid, HFlow, Icon, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { AccordionDataTable } from 'components/accordion/accordion-data-table/AccordionDataTable'
import { useAlert } from 'components/alert'
import { DropdownButton } from 'components/dropdown'
import { useErrorHandler } from 'components/error'
import { CheckboxField, FormDebouncedValueSpy, SubmitlessForm, TextField } from 'components/form'
import { HLabel } from 'components/HLabel'
import { Cnpj, Cpf } from 'components/label'
import { confirm } from 'components/modals/confirm'
import { TableBox, usePagedTableProps } from 'components/table'
import { useInativarSistemaExternoMutation } from 'graphql/hooks.generated'
import { PageParams, SistemaExternoQueryInput, SistemasExternosQuery, TipoPessoaEnum } from 'graphql/types.generated'
import { metaPath } from 'util/metaPath'
import { stringGrouper } from 'util/string/string-grouper'

import { tipoSistemaExternoToLabel } from './model-sistema-externo'

type SistemaExternoQueryModel = SistemasExternosQuery['sistemasExternos']['content'][0]

interface SistemasExternosTableProps {
  readonly data: SistemasExternosQuery
  readonly loading: boolean
  readonly setFilter: (filter: SistemaExternoQueryInput) => void
  readonly refetch: () => void
}

const TODOS = 'Todos'

export function SistemasExternosTable(props: Readonly<SistemasExternosTableProps>) {
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const theme = useTheme()
  const styles = createStyles(theme)

  const [inativarSistemaExterno] = useInativarSistemaExternoMutation()
  const { data, loading, refetch, setFilter } = props

  const onInativarSistemaSuccess = () => {
    refetch()
    alert('success', 'Sistema externo inativado com sucesso.')
  }

  const onInativarSistema = (item: SistemaExternoQueryModel) =>
    confirm({
      title: 'Deseja inativar o sistema externo?',
      body: <InativarSistemaModalBody item={item} />,
      type: 'danger',
      confirmLabel: 'Inativar',
      onConfirm: () =>
        inativarSistemaExterno({ variables: { id: item.id } })
          .then(() => {
            onInativarSistemaSuccess()
          })
          .catch(handleRejection),
    })()

  const tableProps = usePagedTableProps<SistemasExternosQuery['sistemasExternos'], { pageParams?: PageParams }>({
    loading,
    result: data?.sistemasExternos,
    onChange: setFilter,
  })

  const renderAccordionPanel = (item: SistemaExternoQueryModel) => (
    <Grid css={styles.accordionPanel}>
      <Cell size={12}>
        <VFlow style={{ padding: '1rem 0rem' }} vSpacing={0.5}>
          <HLabel title='Municípios vinculados:'>
            <Text>{item.municipios?.length ? stringGrouper(...item.municipios.map((m) => m.nome)) : TODOS}</Text>
          </HLabel>
          <HLabel title='Perfis:'>
            <Text>{item.perfis?.length ? stringGrouper(...item.perfis.map((p) => p.nome)) : TODOS}</Text>
          </HLabel>
        </VFlow>
      </Cell>
    </Grid>
  )

  return (
    <TableBox header={<TableHeader onChange={setFilter} />}>
      <AccordionDataTable<SistemaExternoQueryModel>
        components={{ AccordionPanel: (item) => renderAccordionPanel(item.row) }}
        columns={[
          {
            name: 'nome',
            header: 'Nome',
            render: (item: SistemaExternoQueryModel) => <Text>{item.nome}</Text>,
            size: 2,
          },
          {
            name: 'responsavel',
            header: 'Responsável',
            render: renderResponsavel,
            size: 3,
          },
          {
            name: 'url',
            header: 'URL',
            render: (item: SistemaExternoQueryModel) => <Text style={{ wordBreak: 'break-all' }}>{item.url}</Text>,
            size: 6,
          },
          {
            name: 'localAcesso',
            header: 'Local de acesso',
            render: (item: SistemaExternoQueryModel) => <Text>{tipoSistemaExternoToLabel(item.tipo)}</Text>,
            size: 2,
          },
          {
            name: 'status',
            header: 'Status',
            render: renderStatus,
            size: 2,
          },
          {
            name: 'actions',
            render: (item: SistemaExternoQueryModel) => (
              <DropdownButton>
                <DropdownItem onClick={() => onInativarSistema(item)} disabled={!item.ativo}>
                  Inativar
                </DropdownItem>
              </DropdownButton>
            ),
            size: 1,
          },
        ]}
        {...tableProps}
      />
    </TableBox>
  )
}

const filterPath = metaPath<SistemaExternoQueryInput>()

const TableHeader = ({ onChange }: { onChange: (values: SistemaExternoQueryInput) => void }) => {
  return (
    <SubmitlessForm
      render={() => (
        <form noValidate>
          <FormDebouncedValueSpy onChange={({ values }) => onChange(values)} />
          <Grid gap={1}>
            <Cell size={5} alignSelf='center'>
              <TextField
                name={filterPath.query}
                icon='zoomOutline'
                placeholder='Pesquise por nome do sistema, responsável ou URL'
              />
            </Cell>
            <Cell size={7} alignSelf='center'>
              <CheckboxField name={filterPath.somenteAtivos} label='Exibir somente sistemas ativos' />
            </Cell>
          </Grid>
        </form>
      )}
    />
  )
}

const renderResponsavel = (item: SistemaExternoQueryModel) => (
  <VFlow vSpacing={0} style={{ margin: '0.5rem 0' }}>
    <Text>{item.nomeResponsavel}</Text>
    {item.tipoPessoa === TipoPessoaEnum.FISICA ? (
      <HLabel title='CPF'>
        <Cpf value={item.cpfCnpj} />
      </HLabel>
    ) : (
      <HLabel title='CNPJ'>
        <Cnpj value={item.cpfCnpj} />
      </HLabel>
    )}
    <Text>{item.email}</Text>
  </VFlow>
)

const InativarSistemaModalBody = ({ item }: { item: SistemaExternoQueryModel }) => (
  <VFlow>
    <Text>
      Ao inativar o sistema externo a ação não poderá ser desfeita. Caso necessário, após a inativação cadastre
      novamente o sistema.
    </Text>
    <VFlow vSpacing={0}>
      <HLabel title='Nome do sistema'>{item.nome}</HLabel>
      <HLabel title='Responsável'>{item.nomeResponsavel}</HLabel>
      {item.tipoPessoa === TipoPessoaEnum.FISICA ? (
        <HLabel title='CPF'>
          <Cpf value={item.cpfCnpj} />
        </HLabel>
      ) : (
        <HLabel title='CNPJ'>
          <Cnpj value={item.cpfCnpj} />
        </HLabel>
      )}
      <HLabel title='URL'>{item.url}</HLabel>
      <HLabel title='Local de acesso'>{tipoSistemaExternoToLabel(item.tipo)}</HLabel>
    </VFlow>
  </VFlow>
)

const renderStatus = ({ ativo }: SistemaExternoQueryModel) => (
  <HFlow hSpacing={0.25} alignItems='center'>
    <Icon size={1} fill={ativo ? 'success' : 'danger'} icon={ativo ? 'checkCircleOutline' : 'banOutline'} />
    <Text color={ativo ? 'success' : 'danger'}>{ativo ? 'Ativo' : 'Inativo'}</Text>
  </HFlow>
)

const createStyles = (theme: Theme) => ({
  accordionPanel: css`
    background: ${theme.pallete.gray.c90};
  `,
})
